'use client'

import React, { useState } from 'react'
import { Container, Flex, FlexItem } from '@quark/layout'
import { RadialLayer } from '@quark/layer'
import { Text } from '@quark/typography'
import { useHybridIsMobile } from '@utils/fallbackIsMobile'
import { Button } from '@quark/button'
import { Abha, Ambulance, Fastag, HealthPolicy, Pucc, TrafficFine } from '@quark/icons'
import { AppServiceProps } from '@interfaces/home'
import { useAnimateOnVisible } from '@quark/animation'
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import { NewHomePageComponent } from '@components/HomePage/utils/HomeComponentType'
import { getUrlWithQueryParams } from '@utils/queryParamUtils'
import RatingStars from '@components/CustomerStory/RatingStars/RatingStars'
import { Colors } from '@enums/Colors'
import styles from './appService.module.scss'
import DesktopModal from '../StickyCarousel/DesktopModal'

export const getIcon = name => {
  if (!name) return null
  const icons = {
    TrafficFine: <TrafficFine />,
    Fastag: <Fastag />,
    Pucc: <Pucc />,
    Ambulance: <Ambulance />,
    Abha: <Abha />,
    HealthPolicy: <HealthPolicy />,
  }
  return icons[name] || null
}

const AppService = React.memo((data: AppServiceProps) => {
  const [currImage, setCurrImage] = React.useState(0)
  const [show, setShow] = useState(false)
  const isMobile = useHybridIsMobile(false)
  const { ref, isVisible } = useAnimateOnVisible<HTMLDivElement>({ threshold: 0.4 })
  const hybridIsMobile = useHybridIsMobile(isMobile)
  React.useEffect(() => {
    // INSERT_YOUR_CODE
    if (data?.mediaList && data.mediaList.length > 1) {
      const intervalId = setInterval(() => {
        setCurrImage(prev => {
          const listLength = data.mediaList?.length ?? 0
          const nextIndex = prev + 1 < listLength ? prev + 1 : 1
          return nextIndex
        })
      }, 3000)
      return () => clearInterval(intervalId)
    }
  }, [])

  const handleButtonClick = async (
    eventProperties: {
      eventName?: string
      customProperties?: Record<string, any>
    },
    url: string,
    openModal: boolean
  ) => {
    await trackEvent(eventProperties?.eventName ?? 'btn_explore_insurance', {
      experiment_name: 'wrapper_v1',
      page_type: 'acko_home_page',
      redirection_url: url,
      ...eventProperties?.customProperties,
    })
    if (openModal) {
      setShow(true)
    } else {
      window.location.href = getUrlWithQueryParams(url) || ''
    }
  }

  return (
    <Container
      id={NewHomePageComponent.AppService}
      backgroundColor="base/primary"
      px={{ lg: 'spacing-80', md: 'spacing-32', sm: 'spacing-16' }}
      paddingTop={{ lg: 'spacing-80', md: 'spacing-56', sm: 'spacing-40' }}
      type="root">
      <Flex
        paddingBottom={{ lg: 'spacing-32', md: 'spacing-32', sm: 'spacing-32' }}
        justifyContent="center"
        alignItems="center"
        gap={{ sm: 'spacing-8', md: 'spacing-16', lg: 'spacing-16' }}
        flexDirection="column">
        <Text
          as="div"
          display={{ lg: 'block', md: 'block', sm: 'none' }}
          fontStyle={{ sm: 'um_text-2xl/bold', md: 'um_text-3xl/bold', lg: 'um_text-4xl/bold' }}
          color="base/primary"
          textAlign="center"
          whiteSpace="pre">
          {data?.heading}
        </Text>
        <Text
          as="div"
          display={{ lg: 'none', md: 'none', sm: 'block' }}
          fontStyle={{ sm: 'um_text-2xl/bold', md: 'um_text-3xl/bold', lg: 'um_text-5xl/bold' }}
          color="base/primary"
          textAlign="center"
          whiteSpace="pre">
          {data?.mobileHeading}
        </Text>
        <Flex alignItems="center" flexDirection="column" gap="spacing-32">
          <Text
            as="div"
            fontStyle={{ sm: 'um_text-sm/normal', md: 'um_text-base/normal', lg: 'um_text-xl/normal' }}
            color="base/primary"
            textAlign="center">
            {data?.subHeading}
          </Text>
          <Container display={{ sm: 'none', md: 'block', lg: 'block' }} textAlign="center">
            <Flex flexDirection="column" gap={{ lg: 'spacing-16', md: 'spacing-20' }} alignItems="center">
              <Button
                type="solid"
                size="xl"
                onClick={() => {
                  void handleButtonClick(data?.eventProps || {}, data?.modalData?.link ?? '/', true)
                }}>
                <Flex alignItems="center" justifyContent="center" gap={{ lg: 'spacing-16', md: 'spacing-12' }}>
                  {data?.ctaReviewData?.ctaImageUrl && (
                    <img
                      src={data.ctaReviewData.ctaImageUrl}
                      alt={data.ctaReviewData.ctaImageAlt}
                      className={styles.ctaImage}
                    />
                  )}
                  <Text fontStyle="um_text-base/medium" color="base/inverted primary">
                    {data?.ctaReviewData?.cta || data?.ctaText}
                  </Text>
                </Flex>
              </Button>
              {data?.ctaReviewData?.ratingData && (
                <Flex justifyContent="center" alignItems="center" gap="spacing-20">
                  <RatingStars
                    rating={data.ctaReviewData.ratingData.rating || 4.6}
                    total={data.ctaReviewData.ratingData.total || 5}
                    starColor={Colors.StarColor}
                  />
                  {data.ctaReviewData.ratingData.review?.map(review => (
                    <Flex flexDirection="column" alignItems="center">
                      <Text fontStyle="um_text-3xl/bold">{review?.rating}</Text>
                      <Text fontStyle="um_text-sm/normal">{review?.subText}</Text>
                    </Flex>
                  ))}
                </Flex>
              )}
            </Flex>
          </Container>
        </Flex>
      </Flex>
      {!hybridIsMobile && (
        <Container type="normal" ref={ref}>
          <Flex justifyContent="center" alignItems="center">
            <div className={styles.carousel}>
              <div className={`${styles.card} ${isVisible ? styles.c1 : ''}`}>
                <Flex size="fullSize" alignItems="center" justifyContent="center" position="relative">
                  <Container
                    position="relative"
                    overflow="hidden"
                    backgroundColor="base/elevated"
                    borderRadius="radius-5xl">
                    <RadialLayer
                      blur="40px"
                      position="bottom-right"
                      radius={120}
                      background="rgba(74, 222, 128, 0.32)"
                    />
                    <img className={styles.imageContainerBannerRight} src={data?.leftContainerData?.image ?? ''} />
                    <Container zIndex="content" position="relative" padding="spacing-24" type="fluid" overflow="hidden">
                      <Flex size="fullSize" alignItems="center" position="relative">
                        <Flex
                          position="relative"
                          overflow="hidden"
                          size="fullSize"
                          flexDirection="column"
                          gap="spacing-32"
                          justifyContent="center"
                          alignItems="center">
                          <Text
                            fontStyle="um_text-2xl/bold"
                            color="base/primary"
                            textAlign="center"
                            as="div"
                            whiteSpace="pre">
                            {data?.leftContainerData?.heading}
                          </Text>
                          <Container borderRadius="radius-3xl" zIndex="content" position="relative">
                            <Flex flexDirection="column" gap="spacing-16">
                              <Flex flexDirection="row" gap="spacing-16">
                                <Container
                                  border="solid"
                                  borderColor={currImage === 1 ? 'accent/Green/green-800' : 'base/elevated'}
                                  padding="spacing-16"
                                  backgroundColor="base/primary"
                                  borderRadius="radius-2xl">
                                  <Flex
                                    flexDirection="column"
                                    alignItems="center"
                                    justifyContent="center"
                                    gap="spacing-16">
                                    <Text color="base/primary" fontStyle="um_text-4xl/bold" as="div">
                                      {getIcon(data?.leftContainerData?.topServices?.[0]?.icon)}
                                    </Text>
                                    <Text
                                      color="base/primary"
                                      fontStyle="um_text-base/normal"
                                      as="div"
                                      textAlign="center"
                                      whiteSpace="pre">
                                      {data?.leftContainerData?.topServices?.[0]?.heading}
                                    </Text>
                                  </Flex>
                                </Container>
                                <Container
                                  border="solid"
                                  borderColor={currImage === 2 ? 'accent/Green/green-800' : 'base/elevated'}
                                  padding="spacing-16"
                                  backgroundColor="base/primary"
                                  borderRadius="radius-2xl">
                                  <Flex
                                    flexDirection="column"
                                    alignItems="center"
                                    justifyContent="center"
                                    gap="spacing-16">
                                    <Text color="base/primary" fontStyle="um_text-4xl/bold" as="div">
                                      {getIcon(data?.leftContainerData?.topServices?.[1]?.icon)}
                                    </Text>
                                    <Text
                                      color="base/primary"
                                      fontStyle="um_text-base/normal"
                                      as="div"
                                      textAlign="center"
                                      whiteSpace="pre">
                                      {data?.leftContainerData?.topServices?.[1]?.heading}
                                    </Text>
                                  </Flex>
                                </Container>
                              </Flex>
                              <Flex flexDirection="row" gap="spacing-16">
                                <Container
                                  border="solid"
                                  borderColor={currImage === 3 ? 'accent/Green/green-800' : 'base/elevated'}
                                  padding="spacing-16"
                                  backgroundColor="base/primary"
                                  borderRadius="radius-2xl">
                                  <Flex
                                    flexDirection="column"
                                    alignItems="center"
                                    justifyContent="center"
                                    gap="spacing-16">
                                    <Text color="base/primary" fontStyle="um_text-4xl/bold" as="div">
                                      {getIcon(data?.leftContainerData?.bottomServices?.[0]?.icon)}
                                    </Text>
                                    <Text
                                      color="base/primary"
                                      fontStyle="um_text-base/normal"
                                      as="div"
                                      textAlign="center"
                                      whiteSpace="pre">
                                      {data?.leftContainerData?.bottomServices?.[0]?.heading}
                                    </Text>
                                  </Flex>
                                </Container>
                                <Container padding="spacing-16" borderRadius="radius-2xl" backgroundColor="none">
                                  {/* <img src={data?.leftContainerData?.image ?? ''} /> */}
                                </Container>
                              </Flex>
                            </Flex>
                          </Container>
                        </Flex>
                      </Flex>
                    </Container>
                  </Container>
                </Flex>
              </div>
              <div className={`${styles.card} ${isVisible ? styles.c2 : ''}`}>
                <img className={styles.imageContainer} height="100%" src={data?.mediaList?.[currImage]?.url} />
              </div>
              <div className={`${styles.card} ${isVisible ? styles.c3 : ''}`}>
                <Flex size="fullSize" alignItems="center" justifyContent="center" position="relative">
                  <Container
                    position="relative"
                    overflow="hidden"
                    backgroundColor="base/elevated"
                    borderRadius="radius-5xl">
                    <RadialLayer
                      blur="40px"
                      position="bottom-left"
                      radius={120}
                      background="rgba(236, 72, 153, 0.32)"
                    />
                    <img className={styles.imageContainerBannerLeft} src={data?.rightContainerData?.image ?? ''} />

                    <Container zIndex="content" position="relative" padding="spacing-24" type="fluid" overflow="hidden">
                      <Flex size="fullSize" alignItems="center" position="relative">
                        <Flex
                          position="relative"
                          overflow="hidden"
                          size="fullSize"
                          flexDirection="column"
                          gap="spacing-32"
                          justifyContent="center"
                          alignItems="center">
                          <Text
                            fontStyle="um_text-2xl/bold"
                            color="base/primary"
                            textAlign="center"
                            as="div"
                            whiteSpace="pre">
                            {data?.rightContainerData?.heading}
                          </Text>
                          <Container borderRadius="radius-3xl" zIndex="content" position="relative">
                            <Flex flexDirection="column" gap="spacing-16">
                              <Flex flexDirection="row" gap="spacing-16">
                                <Container
                                  border="solid"
                                  borderColor={currImage === 4 ? 'accent/Pink/pink-800' : 'base/elevated'}
                                  padding="spacing-16"
                                  backgroundColor="base/primary"
                                  borderRadius="radius-2xl">
                                  <Flex
                                    flexDirection="column"
                                    alignItems="center"
                                    justifyContent="center"
                                    gap="spacing-16">
                                    <Text color="base/primary" fontStyle="um_text-4xl/bold" as="div">
                                      {getIcon(data?.rightContainerData?.topServices?.[0]?.icon)}
                                    </Text>
                                    <Text
                                      color="base/primary"
                                      fontStyle="um_text-base/normal"
                                      as="div"
                                      textAlign="center"
                                      whiteSpace="pre">
                                      {data?.rightContainerData?.topServices?.[0]?.heading}
                                    </Text>
                                  </Flex>
                                </Container>
                                <Container
                                  border="solid"
                                  borderColor={currImage === 5 ? 'accent/Pink/pink-800' : 'base/elevated'}
                                  padding="spacing-16"
                                  backgroundColor="base/primary"
                                  borderRadius="radius-2xl">
                                  <Flex
                                    flexDirection="column"
                                    alignItems="center"
                                    justifyContent="center"
                                    gap="spacing-16">
                                    <Text color="base/primary" fontStyle="um_text-4xl/bold" as="div">
                                      {getIcon(data?.rightContainerData?.topServices?.[1]?.icon)}
                                    </Text>
                                    <Text
                                      color="base/primary"
                                      fontStyle="um_text-base/normal"
                                      as="div"
                                      textAlign="center"
                                      whiteSpace="pre">
                                      {data?.rightContainerData?.topServices?.[1]?.heading}
                                    </Text>
                                  </Flex>
                                </Container>
                              </Flex>
                              <Flex flexDirection="row" gap="spacing-16">
                                <Container padding="spacing-16" borderRadius="radius-2xl" backgroundColor="none">
                                  {/* <img src={data?.rightContainerData?.image ?? ''} /> */}
                                </Container>
                                <Container
                                  border="solid"
                                  borderColor={currImage === 6 ? 'accent/Pink/pink-800' : 'base/elevated'}
                                  padding="spacing-16"
                                  backgroundColor="base/primary"
                                  borderRadius="radius-2xl">
                                  <Flex
                                    flexDirection="column"
                                    alignItems="center"
                                    justifyContent="center"
                                    gap="spacing-16">
                                    <Text color="base/primary" fontStyle="um_text-4xl/bold" as="div">
                                      {getIcon(data?.rightContainerData?.bottomServices?.[0]?.icon)}
                                    </Text>
                                    <Text
                                      color="base/primary"
                                      fontStyle="um_text-base/normal"
                                      as="div"
                                      textAlign="center"
                                      whiteSpace="pre">
                                      {data?.rightContainerData?.bottomServices?.[0]?.heading ?? ''}
                                    </Text>
                                  </Flex>
                                </Container>
                              </Flex>
                            </Flex>
                          </Container>
                        </Flex>
                      </Flex>
                    </Container>
                  </Container>
                </Flex>
              </div>
            </div>
          </Flex>
        </Container>
      )}
      {hybridIsMobile && (
        <Container px="spacing-0" paddingBottom={{ sm: 'spacing-40', md: 'spacing-0' }} paddingTop="spacing-0">
          <Flex
            flexDirection={{ sm: 'column', md: 'row', lg: 'row' }}
            gap="spacing-24"
            alignItems={{ md: 'center' }}
            justifyContent={{ md: 'center' }}>
            <FlexItem grow>
              <Flex flexDirection="column" gap="spacing-16">
                <Container
                  overflow="hidden"
                  position="relative"
                  padding="spacing-20"
                  borderRadius="radius-4xl"
                  backgroundColor="base/elevated">
                  <RadialLayer blur="40px" position="top-right" radius={120} background="rgba(236, 72, 153, 0.32)" />
                  <img
                    className={styles.imageContainerBannerRightMobile}
                    width="80px"
                    src={data?.leftContainerData?.image ?? ''}
                  />

                  <Flex
                    position="relative"
                    zIndex="content"
                    flexDirection="column"
                    gap="spacing-20"
                    alignItems="center"
                    justifyContent="center">
                    <Flex flexDirection="row" justifyContent="space-between" alignItems="center" size="fullWidth">
                      <Text fontStyle="um_text-base/bold" color="base/primary" whiteSpace="pre">
                        {data?.leftContainerData?.heading}
                      </Text>
                    </Flex>
                    <Flex flexDirection="row" gap="spacing-8" size="fullWidth">
                      <Container
                        padding={{ sm: 'spacing-0', md: 'spacing-12', lg: 'spacing-12' }}
                        borderRadius="radius-2xl">
                        <Flex flexDirection="column" alignItems="start" justifyContent="center">
                          <Text
                            textAlign="left"
                            color="base/primary"
                            fontStyle={{ sm: 'um_text-2xl/bold', md: 'um_text-3xl/bold', lg: 'um_text-4xl/bold' }}
                            as="div">
                            {getIcon(data?.leftContainerData?.topServices?.[0]?.icon)}
                          </Text>
                          <Text
                            color="base/primary"
                            fontStyle={{ sm: 'um_text-xs/normal', md: 'um_text-xs/normal', lg: 'um_text-xs/normal' }}
                            as="div"
                            textAlign="left"
                            whiteSpace="pre">
                            {data?.leftContainerData?.topServices?.[0]?.heading}
                          </Text>
                        </Flex>
                      </Container>
                      <Container
                        padding={{ sm: 'spacing-0', md: 'spacing-12', lg: 'spacing-12' }}
                        borderRadius="radius-2xl">
                        <Flex flexDirection="column" alignItems="start" justifyContent="center">
                          <Text
                            textAlign="left"
                            color="base/primary"
                            fontStyle={{ sm: 'um_text-2xl/bold', md: 'um_text-3xl/bold', lg: 'um_text-4xl/bold' }}
                            as="div">
                            {getIcon(data?.leftContainerData?.topServices?.[1]?.icon)}
                          </Text>
                          <Text
                            color="base/primary"
                            fontStyle={{ sm: 'um_text-xs/normal', md: 'um_text-xs/normal', lg: 'um_text-xs/normal' }}
                            as="div"
                            textAlign="left"
                            whiteSpace="pre">
                            {data?.leftContainerData?.topServices?.[1]?.heading}
                          </Text>
                        </Flex>
                      </Container>
                      <Container
                        padding={{ sm: 'spacing-0', md: 'spacing-12', lg: 'spacing-12' }}
                        borderRadius="radius-2xl">
                        <Flex flexDirection="column" alignItems="start" justifyContent="center">
                          <Text
                            textAlign="left"
                            color="base/primary"
                            fontStyle={{ sm: 'um_text-2xl/bold', md: 'um_text-3xl/bold', lg: 'um_text-4xl/bold' }}
                            as="div">
                            {getIcon(data?.leftContainerData?.bottomServices?.[0]?.icon)}
                          </Text>
                          <Text
                            color="base/primary"
                            fontStyle={{ sm: 'um_text-xs/normal', md: 'um_text-xs/normal', lg: 'um_text-xs/normal' }}
                            as="div"
                            textAlign="left"
                            whiteSpace="pre">
                            {data?.leftContainerData?.bottomServices?.[0]?.heading}
                          </Text>
                        </Flex>
                      </Container>
                    </Flex>
                  </Flex>
                </Container>
                <Container
                  overflow="hidden"
                  position="relative"
                  padding="spacing-20"
                  borderRadius="radius-4xl"
                  backgroundColor="base/elevated">
                  <RadialLayer blur="40px" position="top-right" radius={120} background="rgba(74, 222, 128, 0.32)" />

                  <img
                    className={styles.imageContainerBannerRightMobile}
                    width="80px"
                    src={data?.rightContainerData?.image ?? ''}
                  />
                  <Flex
                    position="relative"
                    zIndex="content"
                    flexDirection="column"
                    gap="spacing-20"
                    alignItems="center"
                    justifyContent="center">
                    <Flex flexDirection="row" justifyContent="space-between" alignItems="center" size="fullWidth">
                      <Text fontStyle="um_text-base/bold" color="base/primary" whiteSpace="pre">
                        {data?.rightContainerData?.heading}
                      </Text>
                    </Flex>
                    <Flex flexDirection="row" gap="spacing-8" size="fullWidth">
                      <Container
                        padding={{ sm: 'spacing-0', md: 'spacing-12', lg: 'spacing-12' }}
                        borderRadius="radius-2xl">
                        <Flex flexDirection="column" alignItems="start" justifyContent="center">
                          <Text
                            color="base/primary"
                            fontStyle={{ sm: 'um_text-2xl/bold', md: 'um_text-3xl/bold', lg: 'um_text-4xl/bold' }}
                            as="div"
                            textAlign="left">
                            {getIcon(data?.rightContainerData?.topServices?.[0]?.icon)}
                          </Text>
                          <Text
                            color="base/primary"
                            fontStyle={{ sm: 'um_text-xs/normal', md: 'um_text-xs/normal', lg: 'um_text-xs/normal' }}
                            as="div"
                            textAlign="left"
                            whiteSpace="pre">
                            {data?.rightContainerData?.topServices?.[0]?.heading}
                          </Text>
                        </Flex>
                      </Container>
                      <Container
                        padding={{ sm: 'spacing-0', md: 'spacing-12', lg: 'spacing-12' }}
                        borderRadius="radius-2xl">
                        <Flex flexDirection="column" alignItems="start" justifyContent="center">
                          <Text
                            color="base/primary"
                            fontStyle={{ sm: 'um_text-2xl/bold', md: 'um_text-3xl/bold', lg: 'um_text-4xl/bold' }}
                            as="div"
                            textAlign="left">
                            {getIcon(data?.rightContainerData?.topServices?.[1]?.icon)}
                          </Text>
                          <Text
                            color="base/primary"
                            fontStyle={{ sm: 'um_text-xs/normal', md: 'um_text-xs/normal', lg: 'um_text-xs/normal' }}
                            as="div"
                            textAlign="left"
                            whiteSpace="pre">
                            {data?.rightContainerData?.topServices?.[1]?.heading}
                          </Text>
                        </Flex>
                      </Container>
                      <Container
                        padding={{ sm: 'spacing-0', md: 'spacing-12', lg: 'spacing-12' }}
                        borderRadius="radius-2xl">
                        <Flex flexDirection="column" alignItems="start" justifyContent="center">
                          <Text
                            color="base/primary"
                            fontStyle={{ sm: 'um_text-2xl/bold', md: 'um_text-3xl/bold', lg: 'um_text-4xl/bold' }}
                            as="div"
                            textAlign="left">
                            {getIcon(data?.rightContainerData?.bottomServices?.[0]?.icon)}
                          </Text>
                          <Text
                            color="base/primary"
                            fontStyle={{ sm: 'um_text-xs/normal', md: 'um_text-xs/normal', lg: 'um_text-xs/normal' }}
                            as="div"
                            textAlign="left"
                            whiteSpace="pre">
                            {data?.rightContainerData?.bottomServices?.[0]?.heading}
                          </Text>
                        </Flex>
                      </Container>
                    </Flex>
                  </Flex>
                </Container>
              </Flex>
            </FlexItem>
            <FlexItem alignSelf={{ sm: 'center', md: 'flex-end', lg: 'center' }}>
              <Flex size="fullWidth" alignItems="center" justifyContent="center">
                <img className={styles.imageContainer} height="100%" src={data?.mediaList?.[currImage]?.url ?? ''} />
              </Flex>
            </FlexItem>
          </Flex>
          <Container display={{ sm: 'block', md: 'none', lg: 'none' }}>
            <Flex flexDirection="column" alignItems="center" justifyContent="center" gap="spacing-16">
              <Button
                display={{ sm: 'block', md: 'none', lg: 'none' }}
                type="solid"
                size="block"
                onClick={() => {
                  void handleButtonClick(data?.eventProps || {}, data?.modalData?.link ?? '/', false)
                }}>
                <Flex alignItems="center" justifyContent="center" gap="spacing-12">
                  {data?.ctaReviewData?.ctaImageUrl && (
                    <img
                      src={data.ctaReviewData.ctaImageUrl}
                      alt={data.ctaReviewData.ctaImageAlt}
                      className={styles.ctaImage}
                    />
                  )}
                  <Text fontStyle="um_text-base/medium" color="base/inverted primary">
                    {data?.ctaReviewData?.cta || data?.ctaText}
                  </Text>
                </Flex>
              </Button>
              {data?.ctaReviewData?.ratingData && (
                <Flex justifyContent="center" alignItems="center" gap="spacing-12">
                  <RatingStars
                    rating={data.ctaReviewData.ratingData.rating || 4.6}
                    total={data.ctaReviewData.ratingData.total || 5}
                    starColor={Colors.StarColor}
                  />
                  {data.ctaReviewData.ratingData.review?.map(review => (
                    <Flex flexDirection="column" alignItems="center">
                      <Text fontStyle="um_text-3xl/bold">{review?.rating}</Text>
                      <Text fontStyle="um_text-xs/normal">{review?.subText}</Text>
                    </Flex>
                  ))}
                </Flex>
              )}
            </Flex>
          </Container>
        </Container>
      )}
      <DesktopModal show={show} setShow={setShow} modalData={data?.modalData} />
    </Container>
  )
})
export default AppService
